import {DestroyRef, inject, Injectable} from "@angular/core";
import {
    BehaviorSubject,
    combineLatest,
    concatMap,
    forkJoin,
    interval,
    merge,
    Observable,
    of,
    pairwise,
    Subject,
    switchMap,
} from "rxjs";
import {HttpClient} from "@angular/common/http";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {debounceTime, distinctUntilChanged, filter, map, shareReplay, startWith, tap} from "rxjs/operators";
import {isDeepEqual, isShallowEqual} from "remeda";
import {environment} from "../../../environments/environment";
import {LorenAsset} from "../loren-assets/interfaces/loren-asset.interface";
import {LorenAssetProperty} from "../loren-assets/interfaces/loren-asset-property.interface";
import {LorenAssetService} from "../loren-assets/services/loren-asset-service/loren-asset.service";
import {LorenImage} from "../../shared/services/loren-image-service/interfaces/loren-image.interface";
import {LorenImageService} from "../../shared/services/loren-image-service/loren-image.service";
import {
    LorenAuthenticationService
} from "../../shared/services/loren-authentication-service/loren-authentication.service";


/**
 * Also used for update
 */
export interface CreateAssetDTO {
    name: string;
    description: string;
    typeId: number;
    defaultLocation: {
        latitude: number;
        longitude: number;
    };
    propertiesData: {
        propertyTypeId: number;
        value: string;
    }[];
    belongsToGroups: number[];
    sensors: {
        sensorId: number;
        meteringDescription: string;
        meteringContext?: string;
    }[];
}

@Injectable({providedIn: "root"})
export class AssetService {
    private readonly destroyRef = inject(DestroyRef);
    private readonly http = inject(HttpClient);
    private readonly lorenAssetService = inject(LorenAssetService);
    private readonly lorenAuthService = inject(LorenAuthenticationService);
    protected imageService = inject(LorenImageService);

    private readonly active$ = new BehaviorSubject<number[]>([]);
    private readonly refresh$ = new Subject<void>();
    private readonly refCount = new Map<number, number>();

    private readonly allAssetsIds$ = this.lorenAuthService.getUserInfoSubscription().pipe(
        switchMap(() => this.http.get<{ items: LorenAsset[] }>(`${environment.apiServerUrl}/assets`)),
        map(x => x.items.map(asset => asset.id)),
        distinctUntilChanged((previous, current) => isDeepEqual(previous, current)),
        shareReplay({bufferSize: 1, refCount: true})
    );

    private readonly newActive$ = this.active$.pipe(
        pairwise(),
        filter(([prev, curr]) => !isShallowEqual(curr, prev)),
        startWith([]),
        debounceTime(200)
    );
    private interval$ = interval(30_000);

    private readonly properties$ = this.http.get<{
        items: LorenAssetProperty[]
    }>(`${environment.apiServerUrl}/property-types`).pipe(
        map(({items}) => items),
        shareReplay(1),
    )


    private data$ = merge(
        this.refresh$,
        this.newActive$,
        this.interval$,
    ).pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.active$),
        concatMap(ids => {
            return this.http.get<{ items: LorenAsset[] }>(`${environment.apiServerUrl}/assets`, {
                params: {ids}
            })
        }),
        // TODO: The BE needs to give signed URLs for those, so we don't have to fetch them
        switchMap(({items}) => {
            return forkJoin(
                items.map((x) => this.lorenAssetService.getAssetImageObservable(x.image))
            ).pipe(
                map((avatars) => ({
                    items: items.map((item, i) => ({...item, avatar: avatars[i]}))
                }))
            );
        }),
        shareReplay({bufferSize: 1, refCount: true})
    );

    get(id: number) {
        return new Observable<LorenAsset>((observer) => {
            this.updateRefCount(id, 1);

            const sub = this.data$
                .pipe(
                    map(x => x.items.find(e => e.id === id)),
                    distinctUntilChanged((previous, current) => isDeepEqual(previous, current)),
                )
                .subscribe(observer);

            return () => {
                this.updateRefCount(id, -1);
                sub.unsubscribe();
            }
        })
    }

    get all() {
        return this.allAssetsIds$
            .pipe(
                switchMap(ids => {
                    if (ids.length > 0) {
                        return combineLatest(ids.map(id => this.get(id)));
                    }

                    return of([] as LorenAsset[]);
                }),
            );
    }

    get properties() {
        return this.properties$;
    }

    public create(data: CreateAssetDTO) {
        return this.http.post<LorenAsset>(`${environment.apiServerUrl}/assets`, data).pipe(
            tap(() => this.refresh$.next()),
        )
    }

    public edit(id: number, data: CreateAssetDTO) {
        return this.http.put<LorenAsset>(`${environment.apiServerUrl}/assets/${id}`, data).pipe(
            tap(() => this.refresh$.next()),
        )
    }

    updateAssetImage(id: number, content: string) {
        return this.http.post<LorenImage>(`${environment.apiServerUrl}/assets/${id}/images`, {content}).pipe(
            tap(() => this.imageService.clearAllImagesCache())
        );
    }

    private updateRefCount(id: number, delta: number) {
        const currentRefCount = this.refCount.get(id) || 0;
        const nextRefCount = currentRefCount + delta;

        this.refCount.set(id, nextRefCount);

        // If the value is no longer needed, remove it from the active list
        if (nextRefCount === 0) {
            this.active$.next(this.active$.value.filter(e => e !== id));
            return;
        }

        if (!this.active$.value.includes(id)) {
            const sorted = [...this.active$.value, id].sort((a, b) => a - b);
            this.active$.next(sorted);
        }
    }
}